import React, { useCallback, useMemo } from 'react';
import { pdoColumns } from '../piping-anomalies/pdo-piping-anomalies';

import AnomalyDescription from '../report-items/anomalyDescription';
import AnomalyRemarks from '../report-items/anomalyRemarks';
import Conditional from '../../../common/conditional';
import Image from '../../../blocks/report-image/image';
import PDFTablePage from '../../../themed/pdf/pdfTablePage';

import * as models from '../../../../utils/models';
import * as utils from '../../../../utils/utilities';

import css from './style.module.scss';
import appConfig from '../../../../utils/config';
import useProjectDetails from '../../../../hooks/useProjectDetails';

type AnomalyItem = (models.NewAnomalyItem | models.PCNLItemModel) & { class: number; anomalyId: string; anomalyIndex: number; };

interface AnomalyNewProps {
	includePDO?: boolean;
	lineNumberID?: number;
	headers: models.CoverDataItem[];
	data: AnomalyItem[];
}

const AnomalyNew: React.FC<AnomalyNewProps> = ({ includePDO = false, lineNumberID, data, headers }) => {
	const { projectDetails } = useProjectDetails();

	const isAnomalyType = useMemo(() => {
		return projectDetails && utils.getIsAnomalyType(projectDetails);
	}, [projectDetails]);

	const isAnomalyWithCMD = useMemo(() => {
		return projectDetails && utils.getIsAnomalyWithCMD(projectDetails);
	}, [projectDetails]);

	const getAnomalyMapping = useCallback((item: AnomalyItem) => {
		if (isAnomalyWithCMD) {
			return {
				anomalyLabel: 'Condition Class',
				anomalyValue: utils.getAnomalyTypeClassFromAnomaly(item.class.toString())
			};
		}

		if (isAnomalyType) {
			return {
				anomalyLabel: 'Anomaly Type',
				anomalyValue: item.class,
			};
		}
		
		return {
			anomalyLabel: 'Damage Class',
			anomalyValue: item.class,
		};
	}, [isAnomalyType, isAnomalyWithCMD]);

	const getAnomalyStyles = (item: any) => {
		if (isAnomalyType) {
			return utils.getLNGAnomalyClassStyle(item.class);
		}

		if (item.class === undefined) {
			return utils.getPCNLAnomalyStyle(item);
		}

		return utils.getAnomalyClassStyle(item);
	};

	return (
		<div className="p-0">
			<div className="p-1">
				<PDFTablePage data={headers} columns={headerColumns} />
			</div>

			{data.map((dataItem: any, index) => {
				const style = getAnomalyStyles(dataItem);
				const isEven = (index + 1) % 2 === 0;
				
				const { anomalyLabel, anomalyValue } = getAnomalyMapping(dataItem);

				return (
					<React.Fragment>
						<div className={isEven ? 'container-fluid mt-auto' : 'container-fluid'} key={dataItem.id}>
							<div className={utils.concatClassNames([css.anomalyItemRow, 'row p-0'])}>
								<div className="col-4 p-0">
									<div className={utils.concatClassNames(["d-flex"])}>
										<div className={utils.concatClassNames([css.anomalyItemBox, css.anomalyItemGray])}>
											Anomaly Number
										</div>

										<div className={utils.concatClassNames([css.anomalyItemBox, css.anomalyItemGray])}>
											{dataItem.anomalyId}
										</div>
									</div>
									
									<div className={utils.concatClassNames(["d-flex", "h-25"])}>
										<Conditional isVisible={dataItem.class !== undefined}>
												<div className={utils.concatClassNames([css.anomalyItemBox, css.anomalyItemColored, 'flex-fill'])}>
													{anomalyLabel}
												</div>
												<div className={css.anomalyItemBox} style={style}>
													{anomalyValue}
												</div>
										</Conditional>
									</div>

									<Conditional isVisible={dataItem.cmd !== undefined}>
										<div className={utils.concatClassNames(["d-flex", "h-25"])}>
											<div className={utils.concatClassNames([css.anomalyItemBox, css.anomalyItemColored, 'flex-fill'])}>
												CDR
											</div>
											<div className={css.anomalyItemBox} style={style}>
												{dataItem.cmd}
											</div>
										</div>
									</Conditional>

									<div className={utils.concatClassNames(["d-flex", (dataItem.interventionRequired || dataItem.cmd) ? "h-50" : "h-75"])}>
										<div className={css.anomalyItemBox + ' flex-fill'}>Location</div>
										<div className={css.anomalyItemBox}>{dataItem.location}</div>
									</div>

									<Conditional isVisible={!!dataItem.interventionRequired}>
										<div className={utils.concatClassNames(["d-flex", "h-25"])}>
											<div className={css.anomalyItemBox}>Type of Intervention Required</div>
											<div className={css.anomalyItemBox}>{dataItem.interventionRequired}</div>
										</div>
									</Conditional>
								</div>
								<div className="col-4 p-0">
									<div
										className={utils.concatClassNames([
											css.anomalyItemBox,
											css.anomalyItemColored,
										])}
									>
										Comment Description
									</div>
									<div
										style={style}
										className={utils.concatClassNames([
											css.anomalyItemBody,
											css.anomalyItemBodyText,
										])}
									>
										<AnomalyDescription anomalyIndex={dataItem.anomalyIndex} lineNumberID={lineNumberID} anomaly={dataItem} />
									</div>
								</div>
								<div className="col-4 p-0">
									<div
										className={utils.concatClassNames([
											css.anomalyItemBox,
											css.anomalyItemColored,
										])}
									>
										Remark & Recommendation
									</div>
									<div
										style={style}
										className={utils.concatClassNames([
											css.anomalyItemBody,
											css.anomalyItemBodyText,
										])}
									>
										<AnomalyRemarks anomalyIndex={dataItem.anomalyIndex} lineNumberID={lineNumberID} anomaly={dataItem} />
									</div>
								</div>
							</div>

							<Conditional isVisible={appConfig.requireImagesToPublish}>
								<div className={utils.concatClassNames([css.anomalyItemRow, 'row p-0'])}>
									<div className="col-6 p-0">
										<div className={css.anomalyItemHeader}>General View</div>
										<div className={css.anomalyItemBody}>
											{dataItem.generalImage.length > 0 ? (
												<Image
													key={dataItem.generalImage[0].caption}
													src={utils.preventImageCaching(dataItem.generalImage[0].src)}
													alt={dataItem.generalImage[0].caption}
													className={css.anomalyItemImage}
													style={{ borderColor: style.backgroundColor }}
												/>
											) : null}
										</div>
									</div>
									
									<div className="col-6 p-0">
										<div className={css.anomalyItemHeader}>Close View</div>
										<div className={css.anomalyItemBody}>
											{dataItem.closeImage.length > 0 ? (
												<Image
													key={dataItem.closeImage[0].caption}
													src={utils.preventImageCaching(dataItem.closeImage[0].src)}
													alt={dataItem.closeImage[0].caption}
													className={css.anomalyItemImage}
													style={{borderColor: style.backgroundColor}}
												/>
											) : null}
										</div>
									</div>
								</div>

								{dataItem.closeImage.length > 1 && (
									<div className={utils.concatClassNames([css.anomalyItemRow, 'row p-0'])}>
										{dataItem.closeImage[1] && (
											<div className="col-6 p-0">
												<div className={css.anomalyItemHeader}>Close View 2</div>
												<div className={css.anomalyItemBody}>
													<Image
														key={dataItem.closeImage[1].caption}
														src={utils.preventImageCaching(dataItem.closeImage[1].src)}
														alt={dataItem.closeImage[1].caption}
														className={css.anomalyItemImage}
														style={{ borderColor: style.backgroundColor }}
													/>
												</div>
											</div>
										)}

										{dataItem.closeImage[2] && (
											<div className="col-6 p-0">
												<div className={css.anomalyItemHeader}>Close View 3</div>
												<div className={css.anomalyItemBody}>
													<Image
														key={dataItem.closeImage[2].caption}
														src={utils.preventImageCaching(dataItem.closeImage[2].src)}
														alt={dataItem.closeImage[2].caption}
														className={css.anomalyItemImage}
														style={{ borderColor: style.backgroundColor }}
													/>
												</div>
											</div>
										)}
									</div>
								)}
							</Conditional>
						</div>

						<Conditional isVisible={includePDO}>
							<div className="p-1">
								<PDFTablePage data={[dataItem]} columns={pdoColumns} />
							</div>
						</Conditional>
					</React.Fragment>
				);
			})}
		</div>
	);
};

const headerColumns: models.Column[] = [
	{
		path: 'label',
		label: 'Label',
		headerStyle: { display: 'none' },
		style: () => ({ padding: '0.5rem', fontSize: '0.75rem', backgroundColor: '#dfe1e6', textAlign: 'left', textTransform: 'uppercase', fontWeight: '800' }),
	},
	{
		path: 'value',
		label: 'Value',
		headerStyle: { display: 'none' },
		style: () => ({ padding: '0.5rem', fontSize: '0.75rem', textAlign: 'left' }),
	},
];

export default AnomalyNew;
